/**
 * What the settings list may show: the user's role in the current business
 * and the plan's modules.
 *
 * The role comes from the login token (appStore.businessRole). When that's
 * missing — it hid the whole admin section for owners — it is read once from
 * /api/auth/memberships for the current business, the same source the
 * business picker uses. Plan modules come from the billing overview; no
 * module list (older subscriptions) means nothing is locked.
 */
import { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { useAppStore } from '../store/appStore';

type Role = 'owner' | 'admin' | 'manager' | 'member' | 'viewer' | null;

// Per business for the session, so the pane and the screen share one fetch.
const roleCache = new Map<string, Role>();
const modulesCache = new Map<string, string[] | null>();

export function useSettingsAccess() {
  const storeRole = useAppStore((s) => s.businessRole) as Role;
  const businessId = useAppStore((s) => s.currentBusiness?._id);
  const [fetchedRole, setFetchedRole] = useState<Role>(businessId ? roleCache.get(businessId) ?? null : null);
  const [modules, setModules] = useState<string[] | null>(businessId ? modulesCache.get(businessId) ?? null : null);

  useEffect(() => {
    if (!businessId || storeRole) return;
    if (roleCache.has(businessId)) {
      setFetchedRole(roleCache.get(businessId) ?? null);
      return;
    }
    let active = true;
    apiService.getMemberships()
      .then((res) => {
        const card = res.statusCode === 1000 ? (res.data || []).find((m) => m.businessId === businessId) : undefined;
        const role = (card?.role as Role) ?? null;
        roleCache.set(businessId, role);
        if (active) setFetchedRole(role);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [businessId, storeRole]);

  const role: Role = storeRole || fetchedRole;
  const isAdmin = role === 'owner' || role === 'admin';

  useEffect(() => {
    if (!businessId || !isAdmin) return;
    if (modulesCache.has(businessId)) {
      setModules(modulesCache.get(businessId) ?? null);
      return;
    }
    let active = true;
    apiService.getBillingOverview()
      .then((res) => {
        const e = res.statusCode === 1000 ? res.data?.entitlements : undefined;
        const list = e && !e.legacy && Array.isArray(e.modules) ? e.modules : null;
        modulesCache.set(businessId, list);
        if (active) setModules(list);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [businessId, isAdmin]);

  return {
    role,
    isAdmin,
    /** True when the plan includes the module (or the plan has no module list). */
    hasModule: (module?: string) => !module || !modules || modules.includes(module),
  };
}
